// ShoppingBagModal.jsx
import React, { useState, useEffect } from 'react';
import { CloseIcon, WarningIcon } from '../../public/icons';
import { MemoryMagnet } from '../../public/images/home';
import { Link } from 'react-router-dom';

const ShoppingBagModal = ({ onClose }) => {
  const [items, setItems] = useState([]);
  const [shippingFee] = useState(5000);
  const [memoryIncluded] = useState(30000);

  /* ───────── Load cart from localStorage ───────── */
  const loadCart = () => {
    try {
      const raw = localStorage.getItem('memoryMagnetsCart');
      const parsed = raw ? JSON.parse(raw) : [];
      const normalized = parsed.map((item, idx) => ({
        cartItemId: item.cartItemId || `legacy-${idx}`,
        id: item.id || `item-${idx}`,
        title: item.title || item.name || 'Item',
        description: item.description || '',
        quantity: item.quantity || 1,
        price: item.price || 0,
        lineTotal:
          item.lineTotal ?? (item.price || 0) * (item.quantity || 1),
        image: item.image || MemoryMagnet
      }));
      setItems(normalized);
    } catch (err) {
      console.error('Failed to load cart:', err);
      setItems([]);
    }
  };

  useEffect(() => {
    loadCart();

    const handleCartUpdate = () => loadCart();
    window.addEventListener('cartUpdated', handleCartUpdate);
    window.addEventListener('storage', handleCartUpdate);

    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
      window.removeEventListener('storage', handleCartUpdate);
    };
  }, []);

  const persist = (next) => {
    setItems(next);
    localStorage.setItem('memoryMagnetsCart', JSON.stringify(next));
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const updateQuantity = (cartItemId, newQuantity) => {
    if (newQuantity < 1) return;
    const next = items.map((item) =>
      item.cartItemId === cartItemId
        ? {
            ...item,
            quantity: newQuantity,
            lineTotal: item.price * newQuantity
          }
        : item
    );
    persist(next);
  };

  const removeItem = (cartItemId) => {
    const next = items.filter((item) => item.cartItemId !== cartItemId);
    persist(next);
  };

  const subtotal = items.reduce(
    (sum, item) => sum + (item.lineTotal || item.price * item.quantity),
    0
  );
  const total = subtotal + memoryIncluded + shippingFee;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-stretch justify-end z-50">
      <div className="bg-[#FCF7EE] h-full w-full md:w-[500px] flex flex-col">
        {/* Header (fixed at top) */}
        <div className="p-4 flex-shrink-0">
          <div className="flex justify-between items-center border-b border-[#DFDFDF] pb-4">
            <div>
              <h2 className="text-2xl font-medium text-left text-[#000000]">
                Shopping Bag
              </h2>
              <p className="text-[#1C2A46] mb-3 mt-2">
                You have <span className="font-bold">{items.length}</span> item
                {items.length !== 1 ? 's' : ''} in your shopping bag.
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-[#1E1E1E] hover:text-opacity-70 border-2 p-1 border-[#DFDFDF]"
            >
              <CloseIcon className="w-6 h-6 text-[#1E1E1E]" />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-4">
          {/* Empty state */}
          {items.length === 0 && (
            <div className="text-center py-16">
              <p className="text-[#6B635D] text-sm">
                Your shopping bag is empty.
              </p>
              <button
                onClick={onClose}
                className="mt-4 text-[#D2A146] hover:underline text-sm"
              >
                Continue shopping
              </button>
            </div>
          )}

          {/* Cart Items */}
          {items.length > 0 && (
            <div className="space-y-8">
              {items.map((item) => (
                <div
                  key={item.cartItemId}
                  className="border-b border-[#DFDFDF] pb-4 flex gap-4 pt-4"
                >
                  <div>
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-32 h-36 object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-[#1C2A46] mb-1 text-left">
                      {item.title}
                    </h3>
                    {item.description && (
                      <p className="text-[#55000C] italic font-light text-sm mb-2.5 text-left">
                        {item.description}
                      </p>
                    )}

                    <div className="flex-col items-center justify-between">
                      <div className="flex items-center space-x-3 mb-2.5 border rounded border-[#DFDFDF] w-fit">
                        <button
                          onClick={() =>
                            updateQuantity(item.cartItemId, item.quantity - 1)
                          }
                          className="w-8 h-8 flex items-center justify-center text-[#292D32] hover:bg-gray-50"
                        >
                          <span className="text-lg">—</span>
                        </button>
                        <span className="text-[#1C2A46] font-medium">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() =>
                            updateQuantity(item.cartItemId, item.quantity + 1)
                          }
                          className="w-8 h-8 flex items-center justify-center text-[#292D32] hover:bg-gray-50"
                        >
                          <span className="text-xl font-normal">+</span>
                        </button>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-base font-medium text-[#1C2A46]">
                          ₦{(item.price * item.quantity).toLocaleString()}
                        </span>
                        <button
                          onClick={() => removeItem(item.cartItemId)}
                          className="text-sm font-normal text-[#FF383C] hover:underline"
                        >
                          Remove Item
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Order Summary */}
          {items.length > 0 && (
            <div className="space-y-4 mt-12 pb-4">
              <div className="flex justify-between">
                <span className="text-[#666666] text-base font-normal">
                  Subtotal
                </span>
                <span className="text-[#1C2A46] font-normal text-base">
                  ₦{subtotal.toLocaleString()}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-[#666666] text-base font-normal flex items-center">
                  Memory Included
                  <WarningIcon className="w-4 h-4 ml-2" />
                </span>
                <span className="text-[#1C2A46] font-normal text-base">
                  ₦{memoryIncluded.toLocaleString()}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-[#666666] text-base font-normal">
                  Shipping Fee
                </span>
                <span className="text-[#1C2A46] font-normal text-base">
                  ₦{shippingFee.toLocaleString()}
                </span>
              </div>

              <div className="border-t border-[#DFDFDF] my-4"></div>

              <div className="flex justify-between items-center">
                <span className="text-base font-normal text-[#1C2A46]">
                  Total
                </span>
                <span className="text-lg font-medium text-[#1C2A46]">
                  ₦{total.toLocaleString()}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer (pinned at bottom) */}
        {items.length > 0 && (
          <div className="p-4 flex-shrink-0 border-t border-[#DFDFDF] bg-[#FCF7EE] flex flex-col gap-3">
            <Link
              to="/checkout" 
              onClick={onClose}
              className="block w-full text-center bg-primary text-[#FCF7EE] py-4 text-base font-medium hover:bg-opacity-90 transition-colors"
            >
              Proceed to Checkout
            </Link>
            
            {/* ✅ Continue Shopping Button */}
            <button
              onClick={onClose}
              className="block w-full text-center bg-transparent border border-[#1C2A46] text-[#1C2A46] py-4 text-base font-medium hover:bg-[#1C2A46] hover:text-white transition-colors"
            >
              Continue Shopping
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShoppingBagModal;